import React from "react";
import {
  DataGrid,
  GridColDef,
  GridDensity,
  GridFilterModel,
  GridPaginationModel,
  GridRowClassNameParams,
  GridRowModesModel,
  GridRowSelectionModel,
  GridSortModel,
  GridSlots,
  GridSlotProps,
} from "@mui/x-data-grid";
import { SxProps, Theme } from "@mui/material/styles";
import { TableContainer } from "../layouts/Container/TableContainer";

// Define props for the CustomDataGrid component
interface CustomDataGridProps {
  rows: any[]; // Rows can be any type (e.g., IDataInputRow, filteredRows, StockItem[])
  columns: GridColDef[];
  density?: GridDensity;
  onDensityChange?: (newDensity: GridDensity) => void;
  loading?: boolean;
  editMode?: "row" | "cell";
  rowModesModel?: GridRowModesModel;
  pagination?: boolean;
  disableRowSelectionOnClick?: boolean;
  keepNonExistentRowsSelected?: boolean;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (
    newRowSelectionModel: GridRowSelectionModel
  ) => void;
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: (newModel: Record<string, boolean>) => void;
  pageSizeOptions?: number[];
  rowCount?: number;
  paginationMode?: "server" | "client";
  filterMode?: "server" | "client";
  sortingMode?: "server" | "client";
  processRowUpdate?: (newRow: any, oldRow: any) => any;
  onProcessRowUpdateError?: (error: any) => void;
  filterModel?: GridFilterModel;
  onCellClick?: (params: any, event: React.MouseEvent) => void;
  onFilterModelChange?: (model: GridFilterModel) => void;
  onSortModelChange?: (model: GridSortModel) => void;
  disableColumnFilter?: boolean;
  slots?: Partial<GridSlots>;
  slotProps?: Partial<GridSlotProps>;
  initialState?: any;
  sx?: SxProps<Theme>;
  getRowClassName?: (params: GridRowClassNameParams) => string;
  [key: string]: any; // Allow additional props via spread operator
}

const CustomDataGrid: React.FC<CustomDataGridProps> = ({
  rows,
  columns,
  density = "standard", // Default density
  onDensityChange,
  loading = false,
  editMode,
  rowModesModel,
  pagination = false,
  disableRowSelectionOnClick = false,
  keepNonExistentRowsSelected,
  paginationModel,
  onPaginationModelChange,
  rowSelectionModel,
  onRowSelectionModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  pageSizeOptions = [20, 50, 100], // Default page size options
  rowCount,
  paginationMode = "client", // Default to client-side pagination
  filterMode = "client", // Default to client-side filtering
  sortingMode = "client", // Default to client-side sorting
  processRowUpdate,
  onProcessRowUpdateError,
  filterModel,
  onCellClick,
  onFilterModelChange,
  onSortModelChange,
  disableColumnFilter = false,
  slots,
  slotProps,
  initialState,
  sx,
  getRowClassName,
  ...props
}) => {

  return (
    <TableContainer>
      <DataGrid
        rows={rows}
        columns={columns}
        density={density}
        onDensityChange={onDensityChange}
        loading={loading}
        editMode={editMode}
        rowModesModel={rowModesModel}
        pagination
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        keepNonExistentRowsSelected={keepNonExistentRowsSelected}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        pageSizeOptions={pageSizeOptions}
        rowCount={rowCount}
        paginationMode={paginationMode}
        filterMode={filterMode}
        sortingMode={sortingMode}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={onProcessRowUpdateError}
        filterModel={filterModel}
        onCellClick={onCellClick}
        onFilterModelChange={onFilterModelChange}
        onSortModelChange={onSortModelChange}
        disableColumnFilter={disableColumnFilter}
        slots={slots}
        slotProps={slotProps}
        initialState={initialState}
        sx={{
          "& .MuiDataGrid-cell": {
            py: 1,
            display: "flex",
            alignItems: "center", // Vertically center cell content
          },
          ...sx,
        }}
        getRowClassName={getRowClassName}
        {...props}
      />
    </TableContainer>
  );
};

export default CustomDataGrid;
